// Account state. Interface mirrors the reference games' auth/AuthContext.jsx
// exactly — { user, ready, configured, signInWithEmail, signInWithGoogle,
// signUpWithEmail, resendVerification, resetPassword, openEmailAuth,
// signOutUser, deleteAccount } — and the same rule: only an email-verified
// password user, or a Google user, counts as signed in.
//
// GAMZONE_COMMON_CONTRACT.md §4: signup sends a verification mail and signs the
// user back out; sign-in reloads and re-checks emailVerified; sign-out and
// account deletion detach cloud sync but never clear the local guest wallet.
//
// Google sign-in is Android-only (ported from 1941/Coin's AuthContext.jsx):
// it goes through @capacitor-firebase/authentication's native Google Sign-In
// SDK for an ID token, then exchanges that for a Firebase credential — a web
// popup/redirect is deliberately not offered here, matching the other Gamzone
// games (a webview-hosted OAuth popup is unreliable/flagged by Google).

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut as fbSignOut,
  onAuthStateChanged,
  updateProfile,
  reauthenticateWithCredential,
  EmailAuthProvider,
  GoogleAuthProvider,
  signInWithCredential,
  deleteUser,
  type User as FirebaseUser,
} from "firebase/auth";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { doc, deleteDoc } from "firebase/firestore";
import { isFirebaseConfigured } from "../config.ts";
import { auth, db } from "./firebase.ts";
import { attachCloudSync, detachCloudSync } from "../cloudPoints.ts";
import { getLanguage } from "../i18n.ts";
import EmailAuthDialog from "../ui/EmailAuthDialog.tsx";

export interface GameUser {
  uid: string;
  email: string;
  displayName: string;
  emailVerified: true;
}

interface AuthValue {
  user: GameUser | null;
  ready: boolean;
  configured: boolean;
  signInWithEmail: (email: string, password: string) => Promise<GameUser>;
  signInWithGoogle: () => Promise<GameUser>;
  signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<void>;
  resendVerification: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  openEmailAuth: () => void;
  signOutUser: () => Promise<void>;
  deleteAccount: (password: string) => Promise<void>;
}

const AuthCtx = createContext<AuthValue | null>(null);
export function useAuth(): AuthValue {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}

function isApprovedUser(u: FirebaseUser | null): boolean {
  return Boolean(
    u?.emailVerified &&
      u.providerData.some((p) => p.providerId === "password" || p.providerId === "google.com"),
  );
}

function isGoogleUser(u: FirebaseUser): boolean {
  return u.providerData.some((p) => p.providerId === "google.com");
}

async function getGoogleCredential() {
  if (!Capacitor.isNativePlatform()) {
    const error = new Error("GOOGLE_NATIVE_APP_REQUIRED") as Error & { code: string };
    error.code = "auth/google-native-app-required";
    throw error;
  }
  const result = await FirebaseAuthentication.signInWithGoogle({ skipNativeAuth: true });
  const idToken = result.credential?.idToken;
  if (!idToken) {
    const error = new Error("GOOGLE_CREDENTIAL_MISSING") as Error & { code: string };
    error.code = "auth/google-credential-missing";
    throw error;
  }
  return GoogleAuthProvider.credential(idToken, result.credential?.accessToken);
}

function toGameUser(u: FirebaseUser): GameUser {
  return {
    uid: u.uid,
    email: u.email ?? "",
    displayName: u.displayName ?? "",
    emailVerified: true,
  };
}

function applyAuthLanguage(): void {
  if (auth) auth.languageCode = getLanguage() === "en" ? "en" : "ko";
}

/* ----------------------------- provider ----------------------------- */

export function AuthProvider({ children }: { children: ReactNode }) {
  const configured = isFirebaseConfigured();
  const [user, setUser] = useState<GameUser | null>(null);
  const [ready, setReady] = useState(!configured); // nothing to wait for if unconfigured
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  // Track auth state and bind/unbind the cloud points sync to the active user.
  useEffect(() => {
    if (!auth) return undefined;
    const unsub = onAuthStateChanged(auth, (u) => {
      const approved = isApprovedUser(u) ? toGameUser(u!) : null;
      setUser(approved);
      setReady(true);
      if (approved) attachCloudSync(approved.uid);
      else detachCloudSync();
      if (u && !approved) fbSignOut(auth!).catch(() => {});
    });
    return () => unsub();
  }, []);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    if (!auth) throw new Error("firebaseUnconfigured");
    const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
    await credential.user.reload();
    if (!credential.user.emailVerified) {
      await fbSignOut(auth);
      const error = new Error("EMAIL_NOT_VERIFIED") as Error & { code: string };
      error.code = "auth/email-not-verified";
      throw error;
    }
    return toGameUser(credential.user);
  }, []);

  const signInWithGoogle = useCallback(async () => {
    if (!auth) throw new Error("firebaseUnconfigured");
    const credential = await getGoogleCredential();
    const result = await signInWithCredential(auth, credential);
    if (!isApprovedUser(result.user)) {
      await fbSignOut(auth);
      const error = new Error("GOOGLE_EMAIL_NOT_VERIFIED") as Error & { code: string };
      error.code = "auth/google-email-not-verified";
      throw error;
    }
    return toGameUser(result.user);
  }, []);

  const signUpWithEmail = useCallback(
    async (email: string, password: string, displayName = "") => {
      if (!auth) throw new Error("firebaseUnconfigured");
      applyAuthLanguage();
      const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      try {
        const name = displayName.trim();
        if (name) await updateProfile(credential.user, { displayName: name });
        await sendEmailVerification(credential.user);
      } finally {
        // Firebase signs a newly-created account in immediately. Keep unverified
        // accounts out of the game and cloud wallet even if sending mail fails.
        await fbSignOut(auth);
      }
    },
    [],
  );

  const resendVerification = useCallback(async (email: string, password: string) => {
    if (!auth) throw new Error("firebaseUnconfigured");
    applyAuthLanguage();
    const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
    try {
      if (!credential.user.emailVerified) await sendEmailVerification(credential.user);
    } finally {
      await fbSignOut(auth);
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    if (!auth) throw new Error("firebaseUnconfigured");
    applyAuthLanguage();
    await sendPasswordResetEmail(auth, email.trim());
  }, []);

  const openEmailAuth = useCallback(() => setEmailDialogOpen(true), []);

  const signOutUser = useCallback(async () => {
    if (!auth) return;
    await fbSignOut(auth);
  }, []);

  const deleteAccount = useCallback(async (password: string) => {
    if (!auth?.currentUser) throw new Error("firebaseUnconfigured");
    const current = auth.currentUser;
    if (isGoogleUser(current)) {
      await reauthenticateWithCredential(current, await getGoogleCredential());
    } else {
      const credential = EmailAuthProvider.credential(current.email ?? "", password);
      await reauthenticateWithCredential(current, credential);
    }
    // Detach first so a re-sync during deletion doesn't resurrect the doc.
    detachCloudSync();
    if (db) {
      try {
        await deleteDoc(doc(db, "users", current.uid));
      } catch {
        /* Firestore rules may forbid it; account deletion proceeds regardless. */
      }
    }
    await deleteUser(current);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      user, ready, configured,
      signInWithEmail, signInWithGoogle, signUpWithEmail, resendVerification, resetPassword,
      openEmailAuth, signOutUser, deleteAccount,
    }),
    [
      user, ready, configured,
      signInWithEmail, signInWithGoogle, signUpWithEmail, resendVerification, resetPassword,
      openEmailAuth, signOutUser, deleteAccount,
    ],
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
      <EmailAuthDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        configured={configured}
        signInWithEmail={signInWithEmail}
        signInWithGoogle={signInWithGoogle}
        signUpWithEmail={signUpWithEmail}
        resendVerification={resendVerification}
        resetPassword={resetPassword}
      />
    </AuthCtx.Provider>
  );
}
